import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import { AuthContext } from 'context';
import { productService } from 'services/productService';
import axios from 'axios';
import { API_BASE_URL } from 'services/config';

const CATEGORY_LABELS = {
  saas: 'SaaS-Produkte',
  iaas: 'IaaS-Produkte',
  ecm: 'UDS ECM',
  epm: 'UDS EP-Manager',
  auto: 'UDS Automation Pro',
};

function CustomerPriceDialog({ open, onClose, customer }) {
  const { partnerSettings } = useContext(AuthContext);
  const [allProducts, setAllProducts] = useState([]);
  const [prices, setPrices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (!open || !customer) return;
    setIsLoading(true);
    setSaveMessage('');

    Promise.all([
      productService.getProducts(),
      axios.get(API_BASE_URL + '/api/customer-prices/' + customer.id, { withCredentials: true })
    ]).then(function(results) {
      var products = results[0];
      var existingPrices = results[1].data || {};

      // Nur für Partner aktivierte Produkte anzeigen
      var allowedProducts = partnerSettings && (
        partnerSettings.effective_customer_enabled_products || partnerSettings.enabled_products
      );
      var filtered = allowedProducts
        ? products.filter(function(p) { return allowedProducts.indexOf(p.slug) >= 0; })
        : products;

      setAllProducts(filtered);

      // Vorhandene Preise als Initialwert laden
      var initial = {};
      filtered.forEach(function(p) {
        initial[p.slug] = {
          custom_price: (existingPrices[p.slug] && existingPrices[p.slug].custom_price) || '',
          custom_name: (existingPrices[p.slug] && existingPrices[p.slug].custom_name) || '',
        };
      });
      setPrices(initial);
    }).catch(function() {
      setAllProducts([]);
    }).finally(function() {
      setIsLoading(false);
    });
  }, [open, customer, partnerSettings]);

  const handleChange = function(slug, field, value) {
    setPrices(function(prev) {
      var updated = Object.assign({}, prev);
      updated[slug] = Object.assign({}, updated[slug], { [field]: value });
      return updated;
    });
  };

  const handleSave = async function() {
    setIsSaving(true);
    setSaveMessage('');
    try {
      var body = {};
      Object.keys(prices).forEach(function(slug) {
        body[slug] = {
          custom_price: prices[slug].custom_price || null,
          custom_name: prices[slug].custom_name || null,
        };
      });
      await axios.put(API_BASE_URL + '/api/customer-prices/' + customer.id, body, { withCredentials: true });
      setSaveMessage('Preise erfolgreich gespeichert.');
    } catch (err) {
      setSaveMessage('Fehler beim Speichern.');
    } finally {
      setIsSaving(false);
    }
  };

  var productsByCategory = {};
  allProducts.forEach(function(p) {
    if (!productsByCategory[p.category]) productsByCategory[p.category] = [];
    productsByCategory[p.category].push(p);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <SoftTypography variant="h5" fontWeight="medium">
          Individuelle Preise: {customer ? (customer.company || customer.email) : ''}
        </SoftTypography>
        <SoftTypography variant="caption" color="text">
          Lassen Sie Felder leer, um Standardwerte (Partner oder UDS) zu verwenden.
        </SoftTypography>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <SoftBox display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </SoftBox>
        ) : (
          <SoftBox pt={1}>
            {Object.keys(productsByCategory).map(function(category) {
              var prods = productsByCategory[category];
              return (
                <SoftBox key={category} mb={2}>
                  <SoftTypography
                    variant="caption" fontWeight="bold" color="text" display="block" mb={1}
                    style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    {CATEGORY_LABELS[category] || category}
                  </SoftTypography>

                  {/* Spaltenheader */}
                  <Grid container spacing={1} mb={0.5}>
                    <Grid item xs={12} md={4}>
                      <SoftTypography variant="caption" color="secondary" fontWeight="medium">Produkt (Standard)</SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SoftTypography variant="caption" color="secondary" fontWeight="medium">Individueller Name</SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <SoftTypography variant="caption" color="secondary" fontWeight="medium">Individueller Preis</SoftTypography>
                    </Grid>
                  </Grid>

                  {prods.map(function(p) {
                    // Angezeigter Standard = Partner-Custom oder UDS-Standard
                    var customNames = partnerSettings && (partnerSettings.effective_customer_custom_names || partnerSettings.custom_names);
                    var customPrices = partnerSettings && (partnerSettings.effective_customer_custom_prices || partnerSettings.custom_prices);
                    var defaultName = (customNames && customNames[p.slug]) || p.name;
                    var defaultPrice = (customPrices && customPrices[p.slug]) || p.display_price;
                    var entry = prices[p.slug] || { custom_price: '', custom_name: '' };

                    return (
                      <Grid container spacing={1} key={p.slug} alignItems="center" mb={0.75}>
                        <Grid item xs={12} md={4}>
                          <SoftTypography variant="caption" color="text">
                            {defaultName}
                            {defaultPrice ? (
                              <SoftTypography component="span" variant="caption" color="secondary">
                                {' / ' + defaultPrice}
                              </SoftTypography>
                            ) : null}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <SoftInput
                            value={entry.custom_name}
                            onChange={function(e) { handleChange(p.slug, 'custom_name', e.target.value); }}
                            placeholder={defaultName}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <SoftInput
                            value={entry.custom_price}
                            onChange={function(e) { handleChange(p.slug, 'custom_price', e.target.value); }}
                            placeholder={defaultPrice || 'z.B. 19,99 EUR/Monat'}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    );
                  })}

                  <Divider style={{ marginTop: 8 }} />
                </SoftBox>
              );
            })}
          </SoftBox>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', justifyContent: 'space-between' }}>
        <SoftBox>
          {saveMessage !== '' && (
            <SoftTypography variant="body2" color={saveMessage.indexOf('Fehler') >= 0 ? 'error' : 'success'}>
              {saveMessage}
            </SoftTypography>
          )}
        </SoftBox>
        <SoftBox display="flex">
          <SoftBox mr={1}>
            <SoftButton variant="outlined" color="secondary" onClick={onClose} disabled={isSaving}>
              Schließen
            </SoftButton>
          </SoftBox>
          <SoftButton variant="gradient" color="primary" onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? 'Wird gespeichert...' : 'Preise speichern'}
          </SoftButton>
        </SoftBox>
      </DialogActions>
    </Dialog>
  );
}

CustomerPriceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.shape({
    id: PropTypes.number,
    company: PropTypes.string,
    email: PropTypes.string,
  }),
};

CustomerPriceDialog.defaultProps = {
  customer: null,
};

export default CustomerPriceDialog;

